import React, { useState, useEffect } from 'react';
import { MessageCircle, X, Minimize2, Maximize2 } from 'lucide-react';
import { teacherNegotiationService } from '../../firebase/tuition';
import { useRealtime } from '../../contexts/RealtimeContext';
import NegotiationChat from './NegotiationChat';
import { tuitionTeacherService } from '../../firebase/tuition';
import toast from 'react-hot-toast';

const NegotiationWidget = () => {
  const { currentUser } = useRealtime();
  const [negotiations, setNegotiations] = useState([]);
  const [openChats, setOpenChats] = useState({}); // { negotiationId: true/false }
  const [minimizedChats, setMinimizedChats] = useState({}); // { negotiationId: true/false }
  const [loading, setLoading] = useState(true);

  const loadNegotiations = async () => {
    if (!currentUser) return;

    try {
      setLoading(true);
      // Check if user is a teacher
      const teacherStatus = await tuitionTeacherService.getTeacherRegistrationStatus(currentUser.uid);
      const isTeacher = teacherStatus.isRegistered && teacherStatus.status === 'approved';

      let allNegotiations = [];

      if (isTeacher && teacherStatus.teacherId) {
        // Load teacher negotiations
        const teacherNegs = await teacherNegotiationService.getTeacherNegotiations(teacherStatus.teacherId);
        allNegotiations = teacherNegs;
      } else {
        // Load student negotiations
        const studentNegs = await teacherNegotiationService.getStudentNegotiations(currentUser.uid);
        allNegotiations = studentNegs;
      }

      // Filter active negotiations - include accepted for students
      const activeNegotiations = allNegotiations.filter(
        neg => {
          const isStudent = currentUser.uid === neg.studentId;
          // Students should see pending, active, and accepted negotiations
          // Teachers should see pending and active negotiations
          if (isStudent) {
            return neg.status === 'pending' || neg.status === 'active' || neg.status === 'accepted';
          } else {
            return neg.status === 'pending' || neg.status === 'active';
          }
        }
      );

      setNegotiations(activeNegotiations);
    } catch (error) {
      console.error('Error loading negotiations:', error);
      toast.error('Failed to load negotiations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!currentUser) return;

    loadNegotiations();

    // Set up real-time listeners for negotiations
    const unsubscribes = [];

    // Subscribe to each negotiation for real-time updates
    const setupSubscriptions = async () => {
      try {
        const teacherStatus = await tuitionTeacherService.getTeacherRegistrationStatus(currentUser.uid);
        const isTeacher = teacherStatus.isRegistered && teacherStatus.status === 'approved';

        let allNegotiations = [];

        if (isTeacher && teacherStatus.teacherId) {
          const teacherNegs = await teacherNegotiationService.getTeacherNegotiations(teacherStatus.teacherId);
          allNegotiations = teacherNegs;
        } else {
          const studentNegs = await teacherNegotiationService.getStudentNegotiations(currentUser.uid);
          allNegotiations = studentNegs;
        }

        const activeNegotiations = allNegotiations.filter(
          neg => {
            const isStudent = currentUser.uid === neg.studentId;
            if (isStudent) {
              return neg.status === 'pending' || neg.status === 'active' || neg.status === 'accepted';
            } else {
              return neg.status === 'pending' || neg.status === 'active';
            }
          }
        );

        activeNegotiations.forEach(neg => {
          const unsubscribe = teacherNegotiationService.subscribeToNegotiation(
            neg.id,
            (result) => {
              if (result.success && result.data) {
                setNegotiations(prev => {
                  const updated = prev.map(n => 
                    n.id === result.data.id ? { ...n, ...result.data } : n
                  );
                  // Filter based on user type
                  const isStudent = currentUser.uid === result.data.studentId;
                  return updated.filter(n => {
                    if (isStudent) {
                      return n.status === 'pending' || n.status === 'active' || n.status === 'accepted';
                    } else {
                      return n.status === 'pending' || n.status === 'active';
                    }
                  });
                });
              }
            }
          );
          unsubscribes.push(unsubscribe);
        });
      } catch (error) {
        console.error('Error setting up subscriptions:', error);
      }
    };

    setupSubscriptions();

    return () => {
      unsubscribes.forEach(unsub => {
        if (unsub) unsub();
      });
    };
  }, [currentUser]);

  const toggleChat = (negotiationId) => {
    setOpenChats(prev => ({
      ...prev,
      [negotiationId]: !prev[negotiationId]
    }));
    setMinimizedChats(prev => ({
      ...prev,
      [negotiationId]: false
    }));
  };

  const toggleMinimizeChat = (negotiationId) => {
    setMinimizedChats(prev => ({
      ...prev,
      [negotiationId]: !prev[negotiationId]
    }));
  };

  const closeChat = async (negotiationId) => {
    try {
      await teacherNegotiationService.closeNegotiation(negotiationId);
      setOpenChats(prev => {
        const updated = { ...prev };
        delete updated[negotiationId];
        return updated;
      });
      setMinimizedChats(prev => {
        const updated = { ...prev };
        delete updated[negotiationId];
        return updated;
      });
      // Reload negotiations to update the list
      loadNegotiations();
    } catch (error) {
      toast.error('Error closing negotiation: ' + error.message);
    }
  };

  const handlePriceUpdate = (negotiationId, newPrice) => {
    setNegotiations(prev =>
      prev.map(neg =>
        neg.id === negotiationId ? { ...neg, proposedPrice: newPrice } : neg
      )
    );
  };

  if (!currentUser || negotiations.length === 0) {
    return null;
  }

  return (
    <div className="fixed bottom-4 right-4 z-50">
      {/* Chat Icons */}
      <div className="flex flex-col gap-2 items-end">
        {negotiations.map((negotiation) => {
          const isOpen = openChats[negotiation.id];
          const isMinimizedChat = minimizedChats[negotiation.id];
          
          if (isOpen && !isMinimizedChat) {
            return null; // Chat window is open, don't show icon
          }

          return (
            <button
              key={negotiation.id}
              onClick={() => toggleChat(negotiation.id)}
              className="relative bg-blue-600 text-white rounded-full p-3 shadow-lg hover:bg-blue-700 transition-colors"
              title={`${negotiation.status === 'pending' ? 'Pending' : 'Active'} negotiation with ${currentUser.uid === negotiation.studentId ? negotiation.teacherName : negotiation.studentName}`}
            >
              <MessageCircle size={24} />
              {negotiation.status === 'pending' && (
                <span className="absolute -top-1 -right-1 bg-yellow-500 rounded-full w-3 h-3"></span>
              )}
            </button>
          );
        })}
      </div>

      {/* Chat Windows */}
      <div className="flex flex-col gap-2 items-end">
        {negotiations.map((negotiation) => {
          const isOpen = openChats[negotiation.id];
          const isMinimizedChat = minimizedChats[negotiation.id];

          if (!isOpen) return null;

          return (
            <div
              key={negotiation.id}
              className={`bg-white rounded-lg shadow-2xl border border-gray-200 ${
                isMinimizedChat ? 'w-64 h-12' : 'w-96 h-[600px]'
              } transition-all duration-300 flex flex-col`}
            >
              {isMinimizedChat ? (
                <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200 bg-blue-600 text-white rounded-t-lg">
                  <span className="text-sm font-medium truncate">
                    {currentUser.uid === negotiation.studentId 
                      ? negotiation.teacherName 
                      : negotiation.studentName}
                  </span>
                  <div className="flex gap-1">
                    <button
                      onClick={() => toggleMinimizeChat(negotiation.id)}
                      className="p-1 hover:bg-blue-700 rounded"
                      title="Maximize"
                    >
                      <Maximize2 size={16} />
                    </button>
                    <button
                      onClick={() => closeChat(negotiation.id)}
                      className="p-1 hover:bg-blue-700 rounded"
                      title="Close"
                    >
                      <X size={16} />
                    </button>
                  </div>
                </div>
              ) : (
                <div className="relative flex-1 flex flex-col">
                  <NegotiationChat
                    negotiation={negotiation}
                    onClose={() => closeChat(negotiation.id)}
                    onPriceUpdate={(newPrice) => handlePriceUpdate(negotiation.id, newPrice)}
                  />
                  <div className="absolute top-2 right-2 z-10 flex gap-1">
                    <button
                      onClick={() => toggleMinimizeChat(negotiation.id)}
                      className="p-1 bg-white bg-opacity-80 hover:bg-opacity-100 rounded text-gray-700 shadow"
                      title="Minimize"
                    >
                      <Minimize2 size={16} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default NegotiationWidget;

